import { useEffect } from 'react';

/**
 * Tittelen i fanen, satt om igjen når man klikker seg videre.
 *
 * Serveren setter hele hodet – tittel, beskrivelse, kanonisk lenke og
 * delingstagger – før HTML-en sendes ut. Se `apps/api/src/shared/sidehoder.ts`;
 * det er den som gjelder, og den eneste som forhåndsvisninger og søkemotorer
 * får se.
 *
 * Men i en enkeltsideapplikasjon lastes ikke siden på nytt når man går fra
 * forsiden til prislista, og da blir serverens hode stående på en side det
 * ikke lenger hører til. Det er ingen søkemotor som ser det – de henter hver
 * adresse for seg – men det er brukeren, som har fanen sin åpen, og
 * bokmerket de setter mens de står der.
 *
 * Derfor settes to ting her, og bare to: tittelen og den kanoniske lenka.
 * Beskrivelsen og delingstaggene står urørt, fordi ingen leser dem etter at
 * sida er lastet – å oppdatere dem ville vært kode som aldri gjør en forskjell.
 *
 * Titlene er de samme som serverens. Er de ikke det, er det tittelen herfra
 * brukeren ser og serverens som havner i søkeresultatet – to navn på samme
 * side. `lib/sidehode.test.ts` holder dem sammen.
 */
export const SIDETITLER = {
  forside: 'Vår Dag – bryllupsplanlegging gjort enkelt',
  priser: 'Priser – Vår Dag',
  vilkaar: 'Bruksvilkår – Vår Dag',
  salgsbetingelser: 'Salgsbetingelser – Vår Dag',
  angrerett: 'Angreskjema – Vår Dag',
  personvern: 'Personvernerklæring – Vår Dag',
  gjesteinformasjon: 'Til deg som er gjest – Vår Dag',
  sporsmal: 'Vanlige spørsmål – Vår Dag',
  registrer: 'Opprett konto – Vår Dag',
  loggInn: 'Logg inn – Vår Dag',
  /**
   * Bare i nettleseren. Serveren gir ukjente stier `404` og malens tittel, og
   * en side som ikke finnes, har ingen kanonisk adresse å speile.
   */
  ikkeFunnet: 'Siden finnes ikke – Vår Dag',
} as const;

/**
 * Tittelen på et kapittel i brukerveiledningen. Speiler `veiledningstittel` i
 * `apps/api/src/shared/hjelpekapitler.ts`.
 */
export function veiledningstittel(kapitteltittel: string): string {
  return `${kapitteltittel} – Brukerveiledning – Vår Dag`;
}

/**
 * @param tittel Tittelen sida skal ha så lenge den er framme.
 * @param sti Stien den kanoniske lenka skal peke på, for eksempel `/priser`.
 *   Finnes det ingen kanonisk lenke i hodet – appen og feilsiden har ingen –
 *   settes bare tittelen.
 */
export function useSidehode(tittel: string, sti: string): void {
  useEffect(() => {
    const foer = document.title;
    document.title = tittel;

    const lenke = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const foerHref = lenke?.getAttribute('href') ?? null;
    if (lenke) lenke.setAttribute('href', `${window.location.origin}${sti}`);

    // Ryddes opp igjen: uten dette ville tittelen fra prislista blitt stående
    // når man klikker seg videre til en side som ikke setter sin egen.
    return () => {
      document.title = foer;
      if (lenke && foerHref !== null) lenke.setAttribute('href', foerHref);
    };
  }, [tittel, sti]);
}
